// src/app/modules/shared/SkeletonLoader.jsx
// Reusable skeleton preview loader matching the details page layout

import React from 'react';
import './skeleton-loader.scss';

// ── Generic shimmer box ───────────────────────────────────────────────────────
export function Skeleton({ width = '100%', height = 16, radius = 6, style = {} }) {
  return (
    <div
      className="sk-box"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

// ── Full member/staff details page skeleton ───────────────────────────────────
export function MemberDetailsSkeleton() {
  return (
    <div className="sk-details-page">

      {/* Header */}
      <div className="sk-header">
        <div className="sk-back" />
        <div>
          <Skeleton width={180} height={11} radius={4} style={{ marginBottom: 8 }} />
          <Skeleton width={140} height={22} radius={6} />
        </div>
      </div>

      {/* Stats bar */}
      <div className="sk-stats-bar">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="sk-stat-card">
            <div className="sk-icon-box" />
            <div style={{ flex: 1 }}>
              <Skeleton width="60%" height={11} radius={4} style={{ marginBottom: 7 }} />
              <Skeleton width="80%" height={22} radius={5} style={{ marginBottom: 5 }} />
              <Skeleton width="50%" height={10} radius={4} />
            </div>
          </div>
        ))}
      </div>

      {/* Main grid */}
      <div className="sk-main">

        {/* Sidebar */}
        <div className="sk-sidebar">
          {/* Profile hero */}
          <div className="sk-profile-hero">
            <div className="sk-avatar-circle" />
            <Skeleton width={140} height={16} radius={5} style={{ marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
              <Skeleton width={60} height={20} radius={20} />
              <Skeleton width={60} height={20} radius={20} />
            </div>
          </div>

          {/* Chips grid */}
          <div className="sk-chips">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="sk-chip">
                <Skeleton width="55%" height={10} radius={3} style={{ marginBottom: 6 }} />
                <Skeleton width="80%" height={13} radius={4} />
              </div>
            ))}
          </div>

          {/* Contact */}
          <div className="sk-contact">
            <Skeleton width={80} height={10} radius={3} style={{ marginBottom: 10 }} />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="sk-contact-row">
                <div className="sk-contact-icon" />
                <Skeleton width="75%" height={13} radius={4} />
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="sk-actions">
            <Skeleton width="100%" height={36} radius={8} style={{ marginBottom: 8 }} />
            <Skeleton width="100%" height={36} radius={8} />
          </div>
        </div>

        {/* Details col */}
        <div className="sk-detail-col">
          {/* Tab bar */}
          <div className="sk-tab-bar">
            {[100, 90, 80].map((w, i) => (
              <Skeleton key={i} width={w} height={13} radius={4} />
            ))}
          </div>

          {/* Tab content */}
          <div className="sk-tab-content">
            {/* Section 1 */}
            <div className="sk-section">
              <div className="sk-section-head">
                <div className="sk-section-icon" />
                <Skeleton width={140} height={14} radius={4} />
              </div>
              <div className="sk-perf-grid">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="sk-perf-card">
                    <div className="sk-icon-box" />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="70%" height={22} radius={5} style={{ marginBottom: 5 }} />
                      <Skeleton width="55%" height={11} radius={4} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 */}
            <div className="sk-section">
              <div className="sk-section-head">
                <div className="sk-section-icon" />
                <Skeleton width={120} height={14} radius={4} />
              </div>
              <div className="sk-detail-grid">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="sk-detail-box">
                    <Skeleton width="55%" height={10} radius={3} style={{ marginBottom: 7 }} />
                    <Skeleton width="80%" height={13} radius={4} />
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 — role block */}
            <div className="sk-section">
              <div className="sk-section-head">
                <div className="sk-section-icon" />
                <Skeleton width={160} height={14} radius={4} />
              </div>
              <div className="sk-role-block">
                <Skeleton width={80} height={22} radius={20} style={{ marginBottom: 10 }} />
                <Skeleton width="100%" height={13} radius={4} style={{ marginBottom: 6 }} />
                <Skeleton width="85%"  height={13} radius={4} style={{ marginBottom: 6 }} />
                <Skeleton width="70%"  height={13} radius={4} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Visitor details page skeleton ─────────────────────────────────────────────
export function VisitorDetailsSkeleton() {
  return (
    <div className="sk-details-page">

      {/* Header */}
      <div className="sk-header">
        <div className="sk-back" />
        <div>
          <Skeleton width={160} height={11} radius={4} style={{ marginBottom: 8 }} />
          <Skeleton width={140} height={22} radius={6} />
        </div>
      </div>

      {/* Main grid */}
      <div className="sk-main">

        {/* Sidebar */}
        <div className="sk-sidebar">
          <div className="sk-profile-hero sk-profile-hero--light">
            <div className="sk-avatar-circle sk-avatar-circle--light" />
            <Skeleton width={130} height={16} radius={5} style={{ marginBottom: 6 }} />
            <Skeleton width={90}  height={11} radius={4} style={{ marginBottom: 10 }} />
            <Skeleton width={70}  height={20} radius={20} />
          </div>

          {/* Risk card */}
          <div className="sk-risk-card">
            <Skeleton width={80}  height={10} radius={3} style={{ marginBottom: 10 }} />
            <div className="sk-risk-inner">
              <Skeleton width={52} height={36} radius={5} />
              <div style={{ flex: 1 }}>
                <Skeleton width="60%" height={18} radius={20} style={{ marginBottom: 6 }} />
                <Skeleton width="85%" height={11} radius={4} />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="sk-contact">
            <Skeleton width={80} height={10} radius={3} style={{ marginBottom: 10 }} />
            {[...Array(2)].map((_, i) => (
              <div key={i} className="sk-contact-row">
                <div className="sk-contact-icon" />
                <Skeleton width="70%" height={13} radius={4} />
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="sk-contact">
            <Skeleton width={90} height={10} radius={3} style={{ marginBottom: 10 }} />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="sk-stat-row">
                <div className="sk-contact-icon" />
                <div style={{ flex: 1 }}>
                  <Skeleton width="60%" height={14} radius={4} style={{ marginBottom: 3 }} />
                  <Skeleton width="45%" height={11} radius={3} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Details col */}
        <div className="sk-detail-col">
          <div className="sk-tab-bar">
            {[80, 100, 90, 100].map((w, i) => (
              <Skeleton key={i} width={w} height={13} radius={4} />
            ))}
          </div>
          <div className="sk-tab-content">

            {/* Names grid */}
            <div className="sk-section">
              <div className="sk-section-head">
                <div className="sk-section-icon" />
                <Skeleton width={180} height={14} radius={4} />
              </div>
              <div className="sk-names-grid">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="sk-detail-box">
                    <Skeleton width="70%" height={14} radius={4} style={{ marginBottom: 6 }} />
                    <Skeleton width="50%" height={11} radius={3} style={{ marginBottom: 4 }} />
                    <Skeleton width="60%" height={10} radius={3} />
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline summary */}
            <div className="sk-section">
              <div className="sk-section-head">
                <div className="sk-section-icon" />
                <Skeleton width={120} height={14} radius={4} />
              </div>
              <div className="sk-timeline-summary">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="sk-detail-box sk-detail-box--center">
                    <Skeleton width="55%" height={10} radius={3} style={{ marginBottom: 7 }} />
                    <Skeleton width="75%" height={14} radius={4} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}